import type { FC, PropsWithChildren } from 'react';
import { useEffect } from 'react';
import { useNavigate } from 'react-router';
import { SplashScreen } from 'src/components/splash-screen';
import { GetStreamProvider } from 'src/contexts/getstream/getstream-context';
import { useAuthContext, useMeContext } from 'src/hooks/use-context';
import { paths } from 'src/paths';
import { buildSearchParamsUrl } from 'src/utils/search-params';

/**
 * Guard redirects if user is not authenticated
 */
export const AuthGuard: FC<PropsWithChildren> = (props) => {
  const { children } = props;
  const navigate = useNavigate();
  const { authenticated, ...context } = useAuthContext();
  const { profile } = useMeContext();

  useEffect(
    () => {
      if (!authenticated) {
        console.info('[auth-guard] Redirecting (not authenticated)');

        // TODO: move to login page
        if (context.signInRedirect) {
          context.signInRedirect();
          return;
        }

        // redirect to this page after successful login
        navigate(
          buildSearchParamsUrl(paths.auth.firebase.login, {
            returnTo: window.location.href
          })
        );
      }
    },
    // IMPORTANT: redirect only on authentication changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [authenticated]
  );

  // retrieve the user id for GetStream identification
  const getUid = () => {
    if ('firebaseUser' in context) return context.firebaseUser?.uid || null;
    if ('oidcUser' in context) return context.oidcUser?.profile.sub || null;
    // TODO: return keycloak user id
    return undefined;
  };

  const uid = getUid();

  // waiting for authentication or redirect
  if (!authenticated || !profile || uid === null) return <SplashScreen />;

  // guard passed
  if (uid === undefined) return <>{children}</>;
  return <GetStreamProvider uid={uid}>{children}</GetStreamProvider>;
};
